import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  listExpenseUserNotes,
  createExpenseUserNote,
} from "../../api/ExpensesUser/expenseUserNotesApi";
import {
  paymentSummaryChipProps,
  accountingNoteChipFromSummary,
} from "./expenseUserPaymentChips";
import { expenseUserPageShellSx } from "./expenseUserLayout";
import ExpenseUserNotesMobile from "./ExpenseUserNotesMobile";
import {
  pageFieldSx,
  pageHeroShellSx,
  pageContentCardSx,
} from "../../styles/pageModernStyles";

function isSeeAllFromToken() {
  try {
    const tok = localStorage.getItem("token");
    if (!tok) return false;
    const d = jwtDecode(tok);
    const v = d?.permisoVertodoExpenses;
    if (v === true || v === 1) return true;
    if (typeof v === "string" && (v === "1" || v.toLowerCase() === "true")) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

const NOTE_FILTER = {
  ALL: "all",
  PENDING: "pending",
  PARTIAL: "partial",
  COMPLETE: "complete",
};

function filterNotesByAccounting(rows, filter) {
  if (filter === NOTE_FILTER.ALL) return rows;
  return rows.filter((row) => {
    const st = row.accountingSummary?.accountingState;
    if (st == null) return false;
    if (filter === NOTE_FILTER.PENDING) return st === "none";
    if (filter === NOTE_FILTER.PARTIAL) return st === "partial";
    if (filter === NOTE_FILTER.COMPLETE) return st === "all";
    return true;
  });
}

const ExpenseUserNotesPage = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin, isMobile } = useContext(SidebarContext);
  const seeAllNotes = useMemo(() => isSeeAllFromToken(), []);

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [accountingFilter, setAccountingFilter] = useState(NOTE_FILTER.ALL);

  const filteredRows = useMemo(
    () => filterNotesByAccounting(rows, accountingFilter),
    [rows, accountingFilter],
  );

  const fieldSx = useMemo(() => pageFieldSx(theme), [theme]);

  const filterFormSx = useMemo(
    () => ({
      minWidth: { xs: "100%", sm: 220 },
      maxWidth: "100%",
      flex: { xs: "1 1 100%", sm: "0 1 300px" },
      ...pageFieldSx(theme),
    }),
    [theme],
  );

  const mobileRows = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        _chipAcc: accountingNoteChipFromSummary(row.accountingSummary, t),
        _chipPay: paymentSummaryChipProps(row.paymentSummary, t),
        _userLabel:
          row.Owner?.username ||
          row.Owner?.email ||
          (row.UserId != null ? `#${row.UserId}` : t("ExpenseUser.emptyDash")),
      })),
    [filteredRows, t],
  );

  const formatNoteDate = (iso) => {
    if (!iso) return t("ExpenseUser.emptyDash");
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return t("ExpenseUser.emptyDash");
    return d.toLocaleString(i18n.language || "es", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listExpenseUserNotes();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(
        e?.response?.data?.error || e.message || t("ExpenseUser.notes.errList"),
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    const tit = title.trim();
    if (!tit) {
      setError(t("ExpenseUser.notes.errNeedTitle"));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const note = await createExpenseUserNote({
        title: tit,
        description: description.trim() || null,
      });
      setOpen(false);
      setTitle("");
      setDescription("");
      navigate(`/expenses-user/notes/${note.id}`);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.notes.errCreate"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={expenseUserPageShellSx(contentMargin, { maxWidth: 1000 })}>
      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: { xs: "stretch", lg: "flex-start" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", minWidth: 0, flex: 1 }}>
              <ReceiptLongOutlinedIcon
                sx={{
                  color: "primary.main",
                  fontSize: { xs: "2rem", sm: "2.25rem" },
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 1, display: "block", fontWeight: 700 }}
                >
                  CRM
                </Typography>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                  }}
                >
                  {t("ExpenseUser.notes.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, maxWidth: 640 }}
                >
                  {t("sidebar.expensesUser")} ·{" "}
                  {t("ExpenseUser.notes.accounting")}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                alignItems: { sm: "center" },
                gap: 1,
              }}
            >
              {seeAllNotes && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => navigate("/expenses-user/tickets")}
                  sx={{ borderRadius: 2 }}
                  fullWidth={isMobile}
                >
                  {t("ExpenseUser.viewAllTickets")}
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={() => setOpen(true)}
                sx={{ borderRadius: 2 }}
                fullWidth={isMobile}
              >
                {t("ExpenseUser.notes.newNote")}
              </Button>
              <FormControl size="small" sx={filterFormSx}>
                <InputLabel id="notes-accounting-filter-label">
                  {t("ExpenseUser.notes.accounting")}
                </InputLabel>
                <Select
                  labelId="notes-accounting-filter-label"
                  value={accountingFilter}
                  label={t("ExpenseUser.notes.accounting")}
                  onChange={(e) => setAccountingFilter(e.target.value)}
                >
                  <MenuItem value={NOTE_FILTER.ALL}>
                    {t("ExpenseUser.notes.filterAll")}
                  </MenuItem>
                  <MenuItem value={NOTE_FILTER.PENDING}>
                    {t("ExpenseUser.notes.filterPending")}
                  </MenuItem>
                  <MenuItem value={NOTE_FILTER.PARTIAL}>
                    {t("ExpenseUser.notes.filterPartial")}
                  </MenuItem>
                  <MenuItem value={NOTE_FILTER.COMPLETE}>
                    {t("ExpenseUser.notes.filterComplete")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontSize: "0.85rem" }}
      >
        {seeAllNotes
          ? t("ExpenseUser.notes.heroAdmin")
          : t("ExpenseUser.notes.heroUser")}
      </Typography>

      {accountingFilter !== NOTE_FILTER.ALL && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("ExpenseUser.notes.filterResult", {
            filtered: filteredRows.length,
            total: rows.length,
          })}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={22} />
          <span>{t("ExpenseUser.loading")}</span>
        </Box>
      )}

      {!loading && rows.length > 0 && filteredRows.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t("ExpenseUser.notes.noFilterMatch")}
        </Typography>
      )}

      {!loading && rows.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t("ExpenseUser.notes.empty")}
        </Typography>
      )}

      {isMobile ? (
        <ExpenseUserNotesMobile
          theme={theme}
          rows={mobileRows}
          t={t}
          formatNoteDate={formatNoteDate}
          onOpen={(id) => navigate(`/expenses-user/notes/${id}`)}
        />
      ) : (
        filteredRows.map((row) => {
          const chipAcc = accountingNoteChipFromSummary(row.accountingSummary, t);
          const chipPay = paymentSummaryChipProps(row.paymentSummary, t);
          const userLabel =
            row.Owner?.username ||
            row.Owner?.email ||
            (row.UserId != null ? `#${row.UserId}` : t("ExpenseUser.emptyDash"));
          return (
            <Paper
              key={row.id}
              variant="outlined"
              sx={{
                ...pageContentCardSx(theme, theme.palette.primary.main),
                p: 2,
                mb: 1,
                cursor: "pointer",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
              onClick={() => navigate(`/expenses-user/notes/${row.id}`)}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600}>
                  {row.Title || t("ExpenseUser.noTitle")}{" "}
                  <Typography
                    component="span"
                    color="text.secondary"
                    variant="body2"
                  >
                    #{row.id}
                  </Typography>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("ExpenseUser.created")}: {formatNoteDate(row.createdAt)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("ExpenseUser.user")}: {userLabel}
                </Typography>
                {row.Description && String(row.Description).trim() ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontWeight: 600, mb: 0.25 }}
                    >
                      {t("ExpenseUser.description")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {row.Description}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  flexShrink: 0,
                  alignSelf: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Chip
                  size="small"
                  label={chipAcc.label}
                  color={chipAcc.color}
                  variant={chipAcc.variant}
                  onClick={(e) => e.stopPropagation()}
                />
                <Chip
                  size="small"
                  label={chipPay.label}
                  color={chipPay.color}
                  variant={chipPay.variant}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            </Paper>
          );
        })
      )}

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t("ExpenseUser.notes.dialogNewTitle")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("ExpenseUser.notes.fieldTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            sx={fieldSx}
            autoFocus
          />
          <TextField
            label={t("ExpenseUser.notes.fieldDescription")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={fieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            disabled={saving}
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: "divider" }}
            color="inherit"
          >
            {t("ExpenseUser.cancel")}
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={saving}
            sx={{ borderRadius: 2 }}
          >
            {saving
              ? t("ExpenseUser.notes.saving")
              : t("ExpenseUser.notes.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseUserNotesPage;
